import React from 'react';

import { Box, Divider, LinearProgress, Paper, Stack, Typography, useTheme } from '@mui/material';

import { numberWithCommas } from '@clearverify/utils';

import { VerifierType } from 'src/api';
import { ChartBase, ChartOptions } from 'src/components/chart';
import { DashboardPalette } from 'src/constants/dashboard';
import { useDashboardCVAdminData, useDashboardPrintView } from 'src/hooks/dashboard';
import { PermissiblePurposeReferenceForVerifiersDashboardItem } from 'src/types/permissiblePurposeReference';

import { DashboardChartWrapper } from '../DashboardChartWrapper';
import { DashboardVerifierTypeSelect } from '../DashboardVerifierTypeSelect/DashboardVerifierTypeSelect';

import { prepareDataset } from './utils';

export type DashboardPermissiblePurposeBlockProps = {
  permissiblePurposeReferenceData: PermissiblePurposeReferenceForVerifiersDashboardItem[];
  loading?: boolean;
};

export const DashboardPermissiblePurposeBlock: React.FC<DashboardPermissiblePurposeBlockProps> = ({
  permissiblePurposeReferenceData,
  loading,
}) => {
  const theme = useTheme();

  const { isPrintView } = useDashboardPrintView();
  const { verifierType, setVerifierType } = useDashboardCVAdminData();

  const getOptions = React.useCallback(
    (verifierType: VerifierType): ChartOptions => {
      const datasetByVerifierType = prepareDataset(
        permissiblePurposeReferenceData.filter(item => item.verifierType === verifierType),
      );
      return {
        grid: { top: 30, right: 40, bottom: 55, left: 408 },
        animationDuration: isPrintView ? 0 : undefined,
        xAxis: {
          position: 'top',
          minInterval: 5,
          axisLabel: {
            fontSize: theme.typography.body1.fontSize,
            fontWeight: 'bold',
          },
          splitLine: {
            lineStyle: {
              type: [10, 10],
            },
          },
        },
        yAxis: {
          type: 'category',
          inverse: true,
          axisLine: { show: false },
          axisTick: { show: false },
          offset: 400,
          axisLabel: {
            fontSize: theme.typography.body1.fontSize,
            fontFamily: theme.typography.body1.fontFamily,
            width: 400,
            align: 'left',
            overflow: 'truncate',
            color: theme.palette.text.primary,
          },
        },
        tooltip: {
          trigger: 'axis',
          padding: [4, 8, 4, 8],
          backgroundColor: theme.palette.slate?.[500],
          borderColor: theme.palette.slate?.[500],
          textStyle: {
            color: theme.palette.secondary.main,
          },
          // biome-ignore lint/suspicious/noExplicitAny: types don't match real behavior
          formatter: (params: any) => numberWithCommas(params[0].value.count),
        },
        series: [
          {
            type: 'bar',
            encode: {
              x: 'count',
              y: 'purposeValue',
            },
            barMaxWidth: 30,
            itemStyle: {
              borderRadius: 5,
            },
            label: isPrintView ? LABEL_OPTIONS : {},
            color: loading ? DashboardPalette.slate50 : DashboardPalette.green400,
          },
        ],
        dataset: [
          {
            source: datasetByVerifierType,
          },
        ],
      };
    },
    [permissiblePurposeReferenceData, isPrintView, loading, theme],
  );

  const baseOptions = React.useMemo(() => getOptions(verifierType), [getOptions, verifierType]);

  return (
    <Paper sx={{ mt: 2, py: 2.5, px: 3, borderRadius: 2, overflow: 'auto' }} variant="outlined">
      <Stack spacing={2}>
        <Box>
          <Typography variant="subtitle1">Permissible Purposes</Typography>
          <Typography variant="body1" color="text.secondary">
            This chart is a breakdown of permissible purposes from a verifier approval perspective.
          </Typography>
        </Box>
        <Box height="4px">{loading ? <LinearProgress color="secondary" /> : <Divider />}</Box>
        <Box>
          {!isPrintView && (
            <DashboardVerifierTypeSelect
              verifierType={verifierType}
              setVerifierType={setVerifierType}
            />
          )}
          <DashboardChartWrapper isPrintView={isPrintView}>
            {isPrintView ? (
              <React.Fragment>
                <Typography variant="body1" fontWeight="bold">
                  {VerifierType.Commercial}
                </Typography>
                <ChartBase option={getOptions(VerifierType.Commercial)} style={{ height: 555 }} />
                <Typography variant="body1" fontWeight="bold">
                  {VerifierType.Government}
                </Typography>
                <ChartBase option={getOptions(VerifierType.Government)} style={{ height: 555 }} />
              </React.Fragment>
            ) : (
              <ChartBase option={baseOptions} style={{ height: 555 }} />
            )}
          </DashboardChartWrapper>
        </Box>
      </Stack>
    </Paper>
  );
};

const LABEL_OPTIONS = {
  show: true,
  fontSize: '16px',
  fontWeight: 'bold' as const,
  formatter: '{@[1]}',
  position: 'right' as const,
  inside: false,
};
